import { client } from 'graphql/client';
import { LOGOUT_MUTATION } from 'graphql/mutations/auth';

export interface CustomerAuth {
    token: string;
    accountNumber: string;
    mustChangePassword: boolean;
    hasSettlementAccount: boolean;
}

/** RBAC 之外，受结算账户开通状态限制的菜单/路由白名单（resource_path） */
export const SETTLEMENT_RESTRICTED_PATHS = ['/balance', '/usage-records'];

const AUTH_KEY = 'swiftx_customer_auth';

export const saveAuth = (auth: CustomerAuth): void => {
    localStorage.setItem(AUTH_KEY, JSON.stringify(auth));
};

export const getAuth = (): CustomerAuth | null => {
    const data = localStorage.getItem(AUTH_KEY);
    if (!data) return null;
    try {
        return JSON.parse(data) as CustomerAuth;
    } catch {
        return null;
    }
};

export const getToken = (): string | null => {
    return getAuth()?.token || null;
};

/**
 * 清除所有页面级会话状态。
 *
 * <p>各页面用 sessionStorage 缓存查询上下文（筛选条件、分页），这些属于「当前登录用户」的数据，
 * 结束会话时必须整体清掉，避免跨用户残留。整体 clear 而非逐个删 key，后续新增缓存无需再改本方法。
 */
export const clearSessionState = (): void => {
    try {
        sessionStorage.clear();
    } catch {
        /* 隐私模式等场景下可能不可用，清理失败不应阻断登出流程 */
    }
};

/**
 * 清除当前用户的全部本地状态：认证信息 + 页面级会话缓存。
 *
 * <p>两者同属「当前登录用户」，必须一起清。除主动登出外，token 失效（GraphQL UNAUTHORIZED、
 * REST 401）与改密后强制重登也都会走到这里，它们同样是同标签页跳登录页——只清 token 的话，
 * 下一个登录的用户会看到上一个用户输入的运单号等业务单号。
 */
export const removeAuth = (): void => {
    localStorage.removeItem(AUTH_KEY);
    clearSessionState();
};

export const isLoggedIn = (): boolean => {
    return !!getToken();
};

export const isMustChangePassword = (): boolean => {
    return getAuth()?.mustChangePassword === true;
};

export const hasSettlementAccount = (): boolean => {
    return getAuth()?.hasSettlementAccount === true;
};

export const logout = (): void => {
    client.mutate({ mutation: LOGOUT_MUTATION }).catch(() => {
        // 即使后端登出失败，也继续清除本地状态
    }).finally(() => {
        /* removeAuth 已一并清掉页面级会话缓存，无需在此重复调用 clearSessionState */
        removeAuth();
        window.location.href = '/ClientCenter/login';
    });
};
